import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../api";
import { useAuth } from "../auth";
import { Logo, LogoLight } from "../components/Logo";
import { ShieldCheck, KeyRound, Smartphone, CheckCircle2, Copy } from "lucide-react";

export default function AdminLogin() {
  const { refresh } = useAuth();
  const nav = useNavigate();

  // Steps: "password" → "totp_setup" → "totp_setup_verify" | "totp_verify"
  const [step, setStep] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null); // {secret, qr_png_base64, uri}
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/admin/auth/login", { email: email.trim(), password });
      if (data.step === "logged_in") {
        // 2FA not enabled → already logged in
        await refresh();
        nav("/admin", { replace: true });
        return;
      }
      setChallenge(data.challenge);
      if (data.step === "totp_setup_required") {
        const r = await api.post("/admin/auth/totp/setup", { challenge: data.challenge });
        setSetup(r.data);
        setStep("totp_setup");
      } else {
        setStep("totp_verify");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const activateTotp = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/admin/auth/totp/activate", { challenge, code });
      // Logged in
      await refresh();
      nav("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  const verifyTotp = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/admin/auth/totp/verify", { challenge, code });
      await refresh();
      nav("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  const copySecret = () => {
    if (!setup?.secret) return;
    navigator.clipboard.writeText(setup.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ backgroundColor: "var(--auprea-navy)" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div style={{ display: "inline-flex" }}>
            <LogoLight size={36} />
          </div>
          <div style={{ color: "var(--auprea-gold)", fontSize: 12, letterSpacing: "0.3em", textTransform: "uppercase", marginTop: 16, fontWeight: 600 }}>
            Direction
          </div>
        </div>

        <div className="auprea-card" style={{ backgroundColor: "#ffffff", borderColor: "var(--auprea-gold)" }}>
          {step === "password" && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck size={24} color="#caa86a" />
                <h1 style={{ fontSize: 24, margin: 0 }}>Espace direction</h1>
              </div>
              <p style={{ color: "rgba(35,38,46,0.7)", marginBottom: 24, fontSize: 14 }}>
                Accès réservé à la direction d'AUPREA. Authentification à deux facteurs requise.
              </p>
              <form onSubmit={submitPassword} className="space-y-4">
                <div>
                  <label className="auprea-label">Email</label>
                  <input type="email" className="auprea-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" />
                </div>
                <div>
                  <label className="auprea-label">Mot de passe</label>
                  <input type="password" className="auprea-input" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" />
                </div>
                {error && <div style={errorStyle} data-testid="admin-login-error">{error}</div>}
                <button type="submit" className="auprea-btn w-full" disabled={loading} data-testid="admin-login-submit">
                  {loading ? "Vérification…" : "Continuer"}
                </button>
              </form>
            </>
          )}

          {step === "totp_setup" && setup && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <Smartphone size={24} color="#caa86a" />
                <h1 style={{ fontSize: 22, margin: 0 }}>Configurer la double authentification</h1>
              </div>
              <p style={{ color: "rgba(35,38,46,0.7)", marginBottom: 16, fontSize: 14, lineHeight: 1.6 }}>
                Première connexion détectée. Pour sécuriser votre espace, installez{" "}
                <strong>Google Authenticator</strong> (ou Microsoft Authenticator, Authy, 1Password…) et scannez ce QR code.
              </p>

              <div className="my-4 p-4 flex justify-center" style={{ backgroundColor: "#f3efe6" }}>
                <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="QR Code TOTP" style={{ width: 220, height: 220 }} data-testid="totp-qr" />
              </div>

              <div className="mb-4">
                <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(35,38,46,0.6)", marginBottom: 6 }}>
                  Ou saisie manuelle
                </div>
                <div className="flex items-center gap-2">
                  <code data-testid="totp-secret" style={{ flex: 1, padding: "8px 12px", background: "#f5f5f5", fontSize: 13, fontFamily: "monospace", wordBreak: "break-all" }}>{setup.secret}</code>
                  <button type="button" onClick={copySecret} className="auprea-btn-outline" style={{ padding: "8px 12px", minHeight: "auto", fontSize: 12 }} data-testid="btn-copy-secret">
                    {copied ? <CheckCircle2 size={14} color="#2f7d5b" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>

              <form onSubmit={activateTotp} className="space-y-4">
                <div>
                  <label className="auprea-label">Code à 6 chiffres affiché dans Authenticator</label>
                  <input
                    type="text" inputMode="numeric" pattern="[0-9 ]*" maxLength={7}
                    className="auprea-input" value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    placeholder="123 456"
                    style={{ fontSize: 22, letterSpacing: "0.3em", textAlign: "center", fontFamily: "monospace" }}
                    required data-testid="totp-setup-code"
                    autoFocus
                  />
                </div>
                {error && <div style={errorStyle} data-testid="totp-setup-error">{error}</div>}
                <button type="submit" className="auprea-btn w-full" disabled={loading || code.length !== 6} data-testid="totp-setup-submit">
                  {loading ? "Activation…" : "Activer la 2FA et se connecter"}
                </button>
                <p style={{ fontSize: 12, color: "rgba(35,38,46,0.5)", textAlign: "center" }}>
                  💡 Si le code ne fonctionne pas, vérifiez que l'heure de votre téléphone est synchronisée.
                </p>
              </form>
            </>
          )}

          {step === "totp_verify" && (
            <>
              <div className="flex items-center gap-3 mb-2">
                <KeyRound size={24} color="#caa86a" />
                <h1 style={{ fontSize: 24, margin: 0 }}>Code de vérification</h1>
              </div>
              <p style={{ color: "rgba(35,38,46,0.7)", marginBottom: 24, fontSize: 14 }}>
                Ouvrez votre application Authenticator et saisissez le code à 6 chiffres affiché pour AUPREA Direction.
              </p>
              <form onSubmit={verifyTotp} className="space-y-4">
                <div>
                  <input
                    type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                    className="auprea-input" value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    placeholder="• • • • • •"
                    style={{ fontSize: 32, letterSpacing: "0.4em", textAlign: "center", fontFamily: "monospace" }}
                    required data-testid="totp-verify-code"
                    autoFocus
                  />
                </div>
                {error && <div style={errorStyle} data-testid="totp-verify-error">{error}</div>}
                <button type="submit" className="auprea-btn w-full" disabled={loading || code.length !== 6} data-testid="totp-verify-submit">
                  {loading ? "Vérification…" : "Accéder à la direction"}
                </button>
                <div style={{ borderTop: "1px solid rgba(35,38,46,0.08)", marginTop: 20, paddingTop: 16, fontSize: 13, color: "rgba(35,38,46,0.65)", textAlign: "center" }}>
                  Vous n&apos;avez plus accès à votre application Authenticator ?{" "}
                  <button
                    type="button"
                    onClick={() => setStep("reset_request")}
                    style={{ color: "var(--auprea-blue)", fontWeight: 600, textDecoration: "underline" }}
                    data-testid="totp-reset-link"
                  >
                    Réinitialiser ma 2FA par email
                  </button>
                </div>
              </form>
            </>
          )}

          {step === "reset_request" && (
            <ResetRequestForm email={email} password={password} onBack={() => setStep("totp_verify")} />
          )}
        </div>

        <div className="text-center mt-6" style={{ color: "rgba(243,239,230,0.5)", fontSize: 12 }}>
          AUPREA — Direction · accès journalisé et chiffré
        </div>
      </div>
    </div>
  );
}

const errorStyle = {
  color: "#b53a3a",
  fontSize: 14,
  padding: "10px 12px",
  backgroundColor: "#fcf3f3",
  border: "1px solid #f0c5c5",
};

function ResetRequestForm({ email: initEmail, password: initPass, onBack }) {
  const [email, setEmail] = useState(initEmail || "");
  const [password, setPassword] = useState(initPass || "");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/admin/auth/totp/reset/request", { email: email.trim(), password });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="reset-2fa-sent">
        <div className="flex items-center gap-3 mb-3">
          <CheckCircle2 size={24} color="#2f7d5b" />
          <h1 style={{ fontSize: 22, margin: 0 }}>Email envoyé</h1>
        </div>
        <p style={{ color: "rgba(35,38,46,0.75)", lineHeight: 1.6, fontSize: 14 }}>
          Si les identifiants sont valides, un email de réinitialisation 2FA vient d&apos;être
          envoyé à <strong>{email}</strong>. Cliquez sur le lien dans cet email (valable 1 heure)
          pour désactiver votre 2FA actuelle, puis revenez ici pour vous reconnecter et
          scanner un nouveau QR code.
        </p>
        <button onClick={onBack} className="auprea-btn-outline w-full mt-5" data-testid="reset-2fa-back">
          Retour à la connexion
        </button>
      </div>
    );
  }

  return (
    <div data-testid="reset-2fa-form">
      <div className="flex items-center gap-3 mb-2">
        <Smartphone size={24} color="#caa86a" />
        <h1 style={{ fontSize: 22, margin: 0 }}>Réinitialiser ma 2FA</h1>
      </div>
      <p style={{ color: "rgba(35,38,46,0.7)", marginBottom: 20, fontSize: 13, lineHeight: 1.55 }}>
        Confirmez vos identifiants. Vous recevrez un email avec un lien pour désactiver votre
        authentification à deux facteurs. Vous pourrez ensuite la re-configurer avec un nouveau
        QR code lors de votre prochaine connexion.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="auprea-label">Email</label>
          <input type="email" className="auprea-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="reset-2fa-email" />
        </div>
        <div>
          <label className="auprea-label">Mot de passe</label>
          <input type="password" className="auprea-input" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-2fa-password" />
        </div>
        {error && <div style={errorStyle}>{error}</div>}
        <button type="submit" className="auprea-btn w-full" disabled={loading} data-testid="reset-2fa-submit">
          {loading ? "Envoi…" : "Envoyer le lien de réinitialisation"}
        </button>
        <button type="button" onClick={onBack} className="auprea-btn-outline w-full" data-testid="reset-2fa-cancel">
          Annuler
        </button>
      </form>
    </div>
  );
}
